import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatApiError } from '../lib/api';
import { ArrowLeft, Eye, EyeOff, Phone, Mail, Calendar } from 'lucide-react';

export default function AuthPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const mode = searchParams.get('mode') === 'waitlist' ? 'waitlist' : 'login';
  const rawRedirect = searchParams.get('redirect');
  const redirect = rawRedirect && rawRedirect !== '/#register-section' ? rawRedirect : '/dashboard';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  const switchMode = (nextMode) => {
    setError('');
    setSearchParams({ mode: nextMode, redirect });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userData = await login(loginData.email, loginData.password);
      if (userData.role === 'admin') {
        navigate('/admin');
      } else {
        navigate(redirect);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-8">
      <div className="w-full max-w-md bg-white border border-[#E7E5E4] rounded-2xl p-6 md:p-8 shadow-sm">
        <Link to="/" className="inline-flex items-center text-[#78716C] hover:text-[#9A3B26] mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to home
        </Link>

        <div className="grid grid-cols-2 gap-2 bg-[#F5F5F4] rounded-xl p-1 mb-6">
          <button
            type="button"
            onClick={() => switchMode('login')}
            className={`py-2 rounded-lg text-sm font-medium transition ${mode === 'login' ? 'bg-white text-[#292524] shadow-sm' : 'text-[#78716C]'}`}
          >
            Login
          </button>
          <button
            type="button"
            onClick={() => switchMode('waitlist')}
            className={`py-2 rounded-lg text-sm font-medium transition ${mode === 'waitlist' ? 'bg-white text-[#292524] shadow-sm' : 'text-[#78716C]'}`}
          >
            Join Waitlist
          </button>
        </div>

        {mode === 'login' ? (
          <>
            <h2 className="font-heading text-3xl font-bold text-[#292524] mb-2">Welcome Back</h2>
            <p className="text-[#78716C] mb-6">Login to continue your learning journey</p>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password">Password</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#9A3B26]"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#9A3B26] hover:bg-[#7A2E1E] text-white rounded-full py-6 text-lg font-medium">
                {loading ? 'Logging in...' : 'Login'}
              </Button>
              <p className="text-center text-sm text-[#78716C]">
                <Link to="/forgot-password" className="hover:text-[#9A3B26] underline underline-offset-2 transition-colors">
                  Forgot password?
                </Link>
              </p>
            </form>

            <p className="mt-6 text-center text-sm text-[#78716C]">
              Not enrolled yet?{' '}
              <button onClick={() => switchMode('waitlist')} className="text-[#9A3B26] font-medium hover:underline">
                Join the waitlist
              </button>
            </p>
          </>
        ) : (
          <>
            <h2 className="font-heading text-3xl font-bold text-[#292524] mb-2">Join the Waitlist</h2>
            <p className="text-[#78716C] mb-6">
              Enrolment for the 21-Day Kalari Sadhana Programme is by invitation. Reach out to us and we'll get you registered for the next batch.
            </p>

            <div className="bg-[#F4EFE6] rounded-2xl p-6 space-y-4 mb-6">
              <div className="flex items-center gap-3 text-sm text-[#292524]">
                <Calendar className="h-4 w-4 text-[#9A3B26] shrink-0" />
                <span>Next batch starts <strong>July 18, 2026</strong></span>
              </div>

              <a
                href="tel:+918089143192"
                className="flex items-center gap-3 bg-white border border-[#E7E5E4] rounded-xl px-5 py-4 hover:border-[#9A3B26] transition-colors group w-full"
              >
                <div className="w-9 h-9 bg-[#9A3B26] rounded-full flex items-center justify-center shrink-0">
                  <Phone className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-[#78716C]">Call or WhatsApp</p>
                  <p className="font-semibold text-[#292524] group-hover:text-[#9A3B26] transition-colors">+91 80891 43192</p>
                </div>
              </a>

              <a
                href="mailto:connect@thekalaripayattu.com"
                className="flex items-center gap-3 bg-white border border-[#E7E5E4] rounded-xl px-5 py-4 hover:border-[#9A3B26] transition-colors group w-full"
              >
                <div className="w-9 h-9 bg-[#9A3B26] rounded-full flex items-center justify-center shrink-0">
                  <Mail className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-[#78716C]">Email us at</p>
                  <p className="font-semibold text-[#292524] group-hover:text-[#9A3B26] transition-colors">connect@thekalaripayattu.com</p>
                </div>
              </a>

              <a
                href="https://wa.me/918089143192?text=Hi%2C%20I%27d%20like%20to%20join%20the%2021-Day%20Kalari%20Sadhana%20Programme"
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white font-semibold rounded-full py-3.5 text-sm transition-colors"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Message us on WhatsApp
              </a>
            </div>

            <p className="text-center text-sm text-[#78716C]">
              Already enrolled?{' '}
              <button onClick={() => switchMode('login')} className="text-[#9A3B26] font-medium hover:underline">
                Login here
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
